/**
 * governance/systemStats.ts
 * Computes LIVE system statistics for the developer dashboard.
 *
 * PHASE 3 MIGRATION: Reduced dependency on legacy rulebookRegistry.
 * QUESTION_TYPE_REGISTRY import kept (shared reference, not legacy).
 *
 * ARCHITECTURE v4.0 — Phase 3, Step 3.4
 */

import { TAXONOMY, getAllModuleScopedSkills } from '../config/taxonomyRegistry'
import { ALL_QUESTIONS_CORE } from '../data/questionLoader'
import { QUESTION_TYPE_REGISTRY } from './questionTypeDefinitions'
import type { GovernanceSystemStats } from './types'
import type { GovernanceAuditReport } from './unifiedValidator'
import type { PipelineBatchResult } from './pipeline/issueContract'

// ── Legacy import with fallback ───────────────────────────────────────────────
let _ruleCount: number | null = null
function getRuleCount(): number {
  if (_ruleCount !== null) return _ruleCount
  try {
    // Lazy import to allow eventual deletion of rulebookRegistry
    const { getAllRuleDefinitions } = require('./rulebookRegistry')
    _ruleCount = getAllRuleDefinitions().length
  } catch {
    _ruleCount = 0 // rulebookRegistry deleted — expected in Phase 3
  }
  return _ruleCount
}

/**
 * Computes live system statistics.
 * @param auditReport Optional pre-computed audit report (legacy pipeline)
 * @param pipelineReport Optional pre-computed pipeline batch result (new pipeline)
 */
export function computeLiveSystemStats(
  auditReport?: GovernanceAuditReport,
  pipelineReport?: PipelineBatchResult,
): GovernanceSystemStats {
  const totalFamilies = TAXONOMY.length
  const totalSkills = getAllModuleScopedSkills().length
  const totalQuestions = ALL_QUESTIONS_CORE.length
  const totalSeeds = totalQuestions
  const totalRules = getRuleCount()
  const totalQuestionTypes = Object.keys(QUESTION_TYPE_REGISTRY).length

  // Prefer new pipeline report, fall back to legacy audit
  const overallQualityScore =
    pipelineReport?.avgScore ?? auditReport?.avgScore ?? null
  const lastAuditTimestamp = pipelineReport || auditReport ? Date.now() : null

  return {
    totalFamilies,
    totalSkills,
    totalQuestions,
    totalSeeds,
    registeredRulebooks: totalRules,
    registeredQuestionTypes: totalQuestionTypes,
    validatorVersion: '4.2.0-pipeline',
    lastAuditTimestamp,
    overallQualityScore,
  }
}

/**
 * Computes detailed breakdown by module.
 */
export function computeModuleStats() {
  const stats: Record<
    string,
    { familyCount: number; questionCount: number; skillCount: number }
  > = {}

  TAXONOMY.forEach((family) => {
    const mod = family.module
    if (!stats[mod]) {
      stats[mod] = { familyCount: 0, questionCount: 0, skillCount: 0 }
    }
    stats[mod].familyCount++
  })

  ALL_QUESTIONS_CORE.forEach((q) => {
    const mod = q.module
    if (stats[mod]) {
      stats[mod].questionCount++
    }
  })

  const skills = getAllModuleScopedSkills()
  skills.forEach((skillKey) => {
    const [mod] = skillKey.split('::')
    if (stats[mod]) {
      stats[mod].skillCount++
    }
  })

  return stats
}
